import React, { useMemo, useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../../../../components/ui/table';
import { Input } from '../../../../../../components/ui/input';
import { Button } from '../../../../../../components/ui/button';
import { MultiSelect } from '../../../../../../components/ui/multi-select';
import {
  Eye, Calendar, User, Tag, Hash,
  Target, Clock, AlertCircle, CheckCircle, Thermometer,
  Filter, Search, X,
} from 'lucide-react';
import type { SessionSummary, SessionStatus } from './dashboardCalculations';
import { fmtDateFull } from './dashboardCalculations';

// ─── shared helpers (match PumpHouseCoatingTable patterns) ───────────────────

const uniq = (arr: string[]) => Array.from(new Set(arr.filter(Boolean)));
const toOptions = (vals: string[]) => vals.map(v => ({ value: v, label: v }));
const PAGE_SIZES = [25, 50, 100, 200];

// ─── status badge ─────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<SessionStatus, string> = {
  pass: 'All pass',
  warn: 'Warning',
  fail: 'Needs review',
};

const STATUS_CLASS: Record<SessionStatus, string> = {
  pass: 'bg-green-50 text-green-700',
  warn: 'bg-yellow-50 text-yellow-700',
  fail: 'bg-red-50 text-red-700',
};

const StatusBadge: React.FC<{ status: SessionStatus }> = ({ status }) => (
  <span className={`${STATUS_CLASS[status]} inline-block text-xs px-2 py-0.5 rounded font-medium`}>
    {STATUS_LABELS[status]}
  </span>
);

/** Matches reference mock: amber when &gt;0, green when 0. */
const ExcursionBadge: React.FC<{ count: number }> = ({ count }) => (
  <span
    className={
      count === 0
        ? 'inline-flex items-center justify-center min-w-[1.5rem] text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-green-50 text-green-700'
        : 'inline-flex items-center justify-center min-w-[1.5rem] text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800'
    }
  >
    {count}
  </span>
);

// ─── column header helper ─────────────────────────────────────────────────────

const ColLabel: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <div className="flex items-center gap-1">
    {icon}
    {children}
  </div>
);

function sessionSearchHaystack(s: SessionSummary): string {
  return [
    s.date,
    fmtDateFull(s.date),
    s.user,
    s.coatingType,
    s.baseBatch,
    s.hardenerBatch,
    String(s.joints),
    String(s.attempts),
    String(s.fpYield),
    s.medianCycle,
    String(s.faultCount),
    s.status,
    s.key,
  ]
    .join(' ')
    .toLowerCase();
}

// ─── component ────────────────────────────────────────────────────────────────

interface Props {
  sessions: SessionSummary[];
  onSelect: (date: string, user: string) => void;
  /** When true and map is provided, an Excursion column is shown (per session vs current limits). */
  limitsActive?: boolean;
  excursionBySessionKey?: Record<string, number>;
}

const SessionsAggregateTable: React.FC<Props> = ({
  sessions,
  onSelect,
  limitsActive = false,
  excursionBySessionKey,
}) => {
  const showExcColumn = limitsActive && excursionBySessionKey != null;
  const colCount = showExcColumn ? 11 : 10;
  const [search, setSearch] = useState('');
  const [userFilter, setUserFilter] = useState<string[]>([]);
  const [coatingFilter, setCoatingFilter] = useState<string[]>([]);
  const [baseBatchFilter, setBaseBatchFilter] = useState<string[]>([]);
  const [hardenerFilter, setHardenerFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  const userOptions = useMemo(
    () => toOptions(uniq(sessions.map(s => s.user))),
    [sessions],
  );
  const coatingOptions = useMemo(
    () => toOptions(uniq(sessions.map(s => s.coatingType))),
    [sessions],
  );
  const baseBatchOptions = useMemo(
    () => toOptions(uniq(sessions.map(s => s.baseBatch))),
    [sessions],
  );
  const hardenerOptions = useMemo(
    () => toOptions(uniq(sessions.map(s => s.hardenerBatch))),
    [sessions],
  );
  const statusOptions = useMemo(
    () => (['pass', 'warn', 'fail'] as SessionStatus[]).map(value => ({
      value,
      label: STATUS_LABELS[value],
    })),
    [],
  );

  const hasActiveFilters =
    !!search.trim() ||
    userFilter.length > 0 ||
    coatingFilter.length > 0 ||
    baseBatchFilter.length > 0 ||
    hardenerFilter.length > 0 ||
    statusFilter.length > 0;

  const handleClearFilters = () => {
    setSearch('');
    setUserFilter([]);
    setCoatingFilter([]);
    setBaseBatchFilter([]);
    setHardenerFilter([]);
    setStatusFilter([]);
    setCurrentPage(1);
  };

  const filteredSessions = useMemo(() => {
    let rows = [...sessions];

    if (userFilter.length > 0)
      rows = rows.filter(s => userFilter.includes(s.user));
    if (coatingFilter.length > 0)
      rows = rows.filter(s => coatingFilter.includes(s.coatingType));
    if (baseBatchFilter.length > 0)
      rows = rows.filter(s => baseBatchFilter.includes(s.baseBatch));
    if (hardenerFilter.length > 0)
      rows = rows.filter(s => hardenerFilter.includes(s.hardenerBatch));
    if (statusFilter.length > 0)
      rows = rows.filter(s => statusFilter.includes(s.status));

    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(s => {
        const hay = [
          sessionSearchHaystack(s),
          showExcColumn ? String(excursionBySessionKey?.[s.key] ?? 0) : '',
        ]
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }

    return rows;
  }, [sessions, search, userFilter, coatingFilter, baseBatchFilter, hardenerFilter, statusFilter, showExcColumn, excursionBySessionKey]);

  useEffect(() => { setCurrentPage(1); }, [search, userFilter, coatingFilter, baseBatchFilter, hardenerFilter, statusFilter]);

  const totalItems = filteredSessions.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageSlice = filteredSessions.slice(
    (safeCurrentPage - 1) * itemsPerPage,
    safeCurrentPage * itemsPerPage,
  );

  if (sessions.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <p className="text-sm text-gray-500">No sessions found.</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-360px)] w-full flex-col gap-4">
      {/* ── filter panel (aligned with detailed table) ─────────────────────── */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="font-medium">Session filters</span>
          </div>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={handleClearFilters} className="text-gray-500 hover:text-gray-700">
                <X className="h-4 w-4 mr-1" />Clear All Filters
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[180px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search sessions…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="w-[200px]">
            <MultiSelect
              options={userOptions}
              selected={userFilter}
              onChange={setUserFilter}
              placeholder="Operator"
            />
          </div>

          <div className="w-[220px]">
            <MultiSelect
              options={coatingOptions}
              selected={coatingFilter}
              onChange={setCoatingFilter}
              placeholder="Coating type"
            />
          </div>

          <div className="w-[200px]">
            <MultiSelect
              options={baseBatchOptions}
              selected={baseBatchFilter}
              onChange={setBaseBatchFilter}
              placeholder="Base batch"
            />
          </div>

          <div className="w-[200px]">
            <MultiSelect
              options={hardenerOptions}
              selected={hardenerFilter}
              onChange={setHardenerFilter}
              placeholder="Hardener batch"
            />
          </div>

          <div className="w-[200px]">
            <MultiSelect
              options={statusOptions}
              selected={statusFilter}
              onChange={setStatusFilter}
              placeholder="Status"
            />
          </div>
        </div>

        {hasActiveFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-2">
            {search.trim() && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                Search: {search}
                <button type="button" onClick={() => setSearch('')} aria-label="Clear search"><X className="h-3 w-3" /></button>
              </span>
            )}
            {userFilter.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                Operator: {userFilter.join(', ')}
                <button type="button" onClick={() => setUserFilter([])} aria-label="Clear operators"><X className="h-3 w-3" /></button>
              </span>
            )}
            {coatingFilter.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                Coating: {coatingFilter.join(', ')}
                <button type="button" onClick={() => setCoatingFilter([])} aria-label="Clear coating"><X className="h-3 w-3" /></button>
              </span>
            )}
            {baseBatchFilter.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                Base: {baseBatchFilter.join(', ')}
                <button type="button" onClick={() => setBaseBatchFilter([])} aria-label="Clear base batch"><X className="h-3 w-3" /></button>
              </span>
            )}
            {hardenerFilter.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                Hardener: {hardenerFilter.join(', ')}
                <button type="button" onClick={() => setHardenerFilter([])} aria-label="Clear hardener"><X className="h-3 w-3" /></button>
              </span>
            )}
            {statusFilter.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                Status: {statusFilter.map(v => STATUS_LABELS[v as SessionStatus] ?? v).join(', ')}
                <button type="button" onClick={() => setStatusFilter([])} aria-label="Clear status"><X className="h-3 w-3" /></button>
              </span>
            )}
          </div>
        )}

      </div>

      {/* ── table ───────────────────────────────────────────────────────────── */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-[#E2E5EA] bg-white">
        <div className="min-h-0 flex-1 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <Table className="min-w-full">
            <TableHeader>
              <TableRow className="border-b">
                <TableHead className="sticky top-0 z-20 bg-gray-50 text-xs font-medium text-gray-600 whitespace-nowrap">
                  <ColLabel icon={<Calendar className="h-3 w-3" />}>Date</ColLabel>
                </TableHead>
                <TableHead className="sticky top-0 z-20 bg-gray-50 text-xs font-medium text-gray-600 whitespace-nowrap">
                  <ColLabel icon={<User className="h-3 w-3" />}>Operator</ColLabel>
                </TableHead>
                <TableHead className="sticky top-0 z-20 bg-gray-50 text-xs font-medium text-gray-600 whitespace-nowrap">
                  <ColLabel icon={<Tag className="h-3 w-3" />}>Base batch</ColLabel>
                </TableHead>
                <TableHead className="sticky top-0 z-20 bg-gray-50 text-xs font-medium text-gray-600 whitespace-nowrap">
                  <ColLabel icon={<Tag className="h-3 w-3" />}>Hardener batch</ColLabel>
                </TableHead>
                <TableHead className="sticky top-0 z-20 bg-gray-50 text-xs font-medium text-gray-600 whitespace-nowrap">
                  <ColLabel icon={<Hash className="h-3 w-3" />}>Joints</ColLabel>
                </TableHead>
                <TableHead className="sticky top-0 z-20 bg-gray-50 text-xs font-medium text-gray-600 whitespace-nowrap">
                  <ColLabel icon={<Target className="h-3 w-3" />}>First pass yield</ColLabel>
                </TableHead>
                <TableHead className="sticky top-0 z-20 bg-gray-50 text-xs font-medium text-gray-600 whitespace-nowrap">
                  <ColLabel icon={<Clock className="h-3 w-3" />}>Cycle</ColLabel>
                </TableHead>
                <TableHead className="sticky top-0 z-20 bg-gray-50 text-xs font-medium text-gray-600 whitespace-nowrap">
                  <ColLabel icon={<AlertCircle className="h-3 w-3" />}>Faults</ColLabel>
                </TableHead>
                {showExcColumn && (
                  <TableHead
                    className="sticky top-0 z-20 bg-gray-50 text-xs font-medium text-gray-600 whitespace-nowrap text-right"
                    title="Temperature excursions vs applied limits (per joint row readings)"
                  >
                    <ColLabel icon={<Thermometer className="h-3 w-3" />}>Exc.</ColLabel>
                  </TableHead>
                )}
                <TableHead className="sticky top-0 z-20 bg-gray-50 text-xs font-medium text-gray-600 whitespace-nowrap">
                  <ColLabel icon={<CheckCircle className="h-3 w-3" />}>Status</ColLabel>
                </TableHead>
                <TableHead className="sticky top-0 z-20 bg-gray-50 text-xs font-medium text-gray-600 whitespace-nowrap text-center w-14">
                  <ColLabel icon={<Eye className="h-3 w-3" />}>Action</ColLabel>
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {pageSlice.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="text-center py-8 text-sm text-gray-500">
                    No sessions match the current filters.
                  </TableCell>
                </TableRow>
              ) : (
                pageSlice.map(s => (
                  <TableRow key={s.key} className="hover:bg-gray-50 border-b border-gray-100">
                    <TableCell className="text-xs text-gray-800 font-medium whitespace-nowrap">
                      {fmtDateFull(s.date)}
                    </TableCell>

                    <TableCell className="text-xs text-gray-600 whitespace-nowrap">{s.user}</TableCell>

                    <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                      <span className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded font-mono text-xs">
                        {s.baseBatch}
                      </span>
                    </TableCell>

                    <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                      <span className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded font-mono text-xs">
                        {s.hardenerBatch}
                      </span>
                    </TableCell>

                    <TableCell className="text-xs font-semibold text-gray-800">
                      {s.joints}
                    </TableCell>

                    <TableCell className={`text-xs font-semibold ${s.fpYield >= 95 ? 'text-green-700' : s.fpYield >= 80 ? 'text-yellow-700' : 'text-red-600'}`}>
                      {s.fpYield.toFixed(1)}%
                    </TableCell>

                    <TableCell className="text-xs text-gray-600">{s.medianCycle}</TableCell>

                    <TableCell>
                      {s.faultCount > 0 ? (
                        <span className="bg-red-50 text-red-700 text-xs px-1.5 py-0.5 rounded font-medium">
                          {s.faultCount}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">0</span>
                      )}
                    </TableCell>

                    {showExcColumn && excursionBySessionKey && (
                      <TableCell className="text-right">
                        <ExcursionBadge count={excursionBySessionKey[s.key] ?? 0} />
                      </TableCell>
                    )}

                    <TableCell>
                      <StatusBadge status={s.status} />
                    </TableCell>

                    <TableCell className="text-center">
                      <span
                        role="button"
                        tabIndex={0}
                        title={`View session — ${s.user} · ${s.date}`}
                        onClick={() => onSelect(s.date, s.user)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onSelect(s.date, s.user);
                          }
                        }}
                        className="inline-flex cursor-pointer text-gray-400 hover:text-gray-700 transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <div className="sticky bottom-0 z-10 flex-shrink-0 border-t border-[#E2E5EA] bg-white/95 px-4 py-3 backdrop-blur">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-6 text-[12px] text-text-grey">
              <div className="flex items-center gap-2">
                <span className="font-medium">Showing:</span>
                <span className="font-semibold text-blueprint-blue">
                  {totalItems > 0
                    ? `${(safeCurrentPage - 1) * itemsPerPage + 1}-${Math.min(safeCurrentPage * itemsPerPage, totalItems)}`
                    : '0'}{' '}
                  of {totalItems}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span>Items per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={e => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="rounded-lg border border-[#E2E5EA] bg-white px-2 py-1 text-[12px] text-text-grey"
                  title="Items per page"
                >
                  {PAGE_SIZES.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={safeCurrentPage === 1} className="hidden md:inline-flex">First</Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safeCurrentPage === 1}>Previous</Button>
                <span className="px-2 text-[12px] text-text-grey">Page {safeCurrentPage} of {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safeCurrentPage === totalPages}>Next</Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={safeCurrentPage === totalPages} className="hidden md:inline-flex">Last</Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionsAggregateTable;
